/**
 * Billing Dashboard Component
 * Displays billing summary and breakdown by matter/streamer
 */

import React from 'react';
import { Card, Button, Stack, Flex, useTheme, spacing, fontSize } from '@fti/design-system';

export interface StreamerStats {
  streamerName: string;
  videoCount: number;
  totalMinutes: number;
  totalCost: number;
}

export interface MatterBillingData {
  matterId: string;
  matterCode: string;
  clientName: string;
  billingRate: number;
  totalMinutes: number;
  totalCost: number;
  streamers: StreamerStats[];
}

export interface BillingSummary {
  totalMatters: number;
  totalStreamers: number;
  totalVideos: number;
  totalMinutes: number;
  totalCost: number;
}

export interface StreamerBillingData {
  streamerName: string;
  matterCode: string;
  clientName: string;
  videoCount: number;
  totalMinutes: number;
  totalCost: number;
}

export interface BillingDashboardProps {
  matterData: MatterBillingData[];
  streamerData: StreamerBillingData[];
  summary: BillingSummary;
  isLoading?: boolean;
  onExportCSV: () => void;
}

/**
 * Dashboard component for billing data
 */
export function BillingDashboard({
  matterData,
  streamerData,
  summary,
  isLoading = false,
  onExportCSV,
}: BillingDashboardProps) {
  const { theme } = useTheme();
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
  const formatMinutes = (minutes: number) => {
    if (minutes < 60) return `${minutes.toFixed(1)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins.toFixed(0)}m`;
  };

  // Loading state
  if (isLoading) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <p style={{ color: mutedColor }}>Loading billing data...</p>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Stack gap="lg">
      {/* Summary Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: spacing.md,
        }}
      >
        <Card variant="outlined">
          <Card.Body>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: textColor }}>
                {summary.totalMatters}
              </div>
              <div style={{ fontSize: fontSize.sm, color: mutedColor }}>Active Matters</div>
            </div>
          </Card.Body>
        </Card>

        <Card variant="outlined">
          <Card.Body>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: textColor }}>
                {summary.totalStreamers}
              </div>
              <div style={{ fontSize: fontSize.sm, color: mutedColor }}>Creators</div>
            </div>
          </Card.Body>
        </Card>

        <Card variant="outlined">
          <Card.Body>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: textColor }}>
                {summary.totalVideos}
              </div>
              <div style={{ fontSize: fontSize.sm, color: mutedColor }}>Videos Processed</div>
            </div>
          </Card.Body>
        </Card>

        <Card variant="outlined">
          <Card.Body>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: textColor }}>
                {formatMinutes(summary.totalMinutes)}
              </div>
              <div style={{ fontSize: fontSize.sm, color: mutedColor }}>Total Duration</div>
            </div>
          </Card.Body>
        </Card>

        <Card variant="elevated">
          <Card.Body>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: fontSize['2xl'],
                  fontWeight: 600,
                  color: theme.colors.semantic.success.default,
                }}
              >
                {formatCurrency(summary.totalCost)}
              </div>
              <div style={{ fontSize: fontSize.sm, color: mutedColor }}>Total Billable</div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Export Button */}
      <Flex justify="end">
        <Button variant="outline" color="primary" onClick={onExportCSV}>
          Export Excel
        </Button>
      </Flex>

      {/* Matter Breakdown Table */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="md">
            <h3 style={{ margin: 0, color: textColor }}>Billing by Matter</h3>

            {matterData.length === 0 ? (
              <p style={{ color: mutedColor, fontStyle: 'italic' }}>No billing data available.</p>
            ) : (
              <div
                style={{
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                {/* Table Header */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 120px 100px 120px 120px',
                    padding: `${spacing.sm} ${spacing.md}`,
                    background: theme.colors.background.paper,
                    borderBottom: `1px solid ${theme.colors.border.default}`,
                    fontSize: fontSize.xs,
                    fontWeight: 600,
                    color: mutedColor,
                    textTransform: 'uppercase',
                  }}
                >
                  <div>Matter / Client</div>
                  <div>Rate</div>
                  <div>Videos</div>
                  <div>Duration</div>
                  <div>Total</div>
                </div>

                {/* Matter Rows */}
                {matterData.map((matter) => (
                  <div
                    key={matter.matterId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 120px 100px 120px 120px',
                      padding: `${spacing.sm} ${spacing.md}`,
                      borderBottom: `1px solid ${theme.colors.border.default}`,
                      alignItems: 'center',
                      fontSize: fontSize.sm,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500, color: textColor }}>{matter.matterCode}</div>
                      <div style={{ fontSize: fontSize.sm, color: mutedColor }}>{matter.clientName}</div>
                    </div>
                    <div style={{ color: mutedColor }}>${matter.billingRate}/min</div>
                    <div style={{ color: textColor }}>
                      {matter.streamers.reduce((sum, s) => sum + s.videoCount, 0)}
                    </div>
                    <div style={{ color: textColor }}>{formatMinutes(matter.totalMinutes)}</div>
                    <div style={{ fontWeight: 500, color: theme.colors.semantic.success.default }}>
                      {formatCurrency(matter.totalCost)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Stack>
        </Card.Body>
      </Card>

      {/* Streamer Breakdown Table */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="md">
            <h3 style={{ margin: 0, color: textColor }}>Billing by Creator</h3>

            {streamerData.length === 0 ? (
              <p style={{ color: mutedColor, fontStyle: 'italic' }}>No streamer data available.</p>
            ) : (
              <div
                style={{
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                {/* Table Header */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 100px 120px 120px',
                    padding: `${spacing.sm} ${spacing.md}`,
                    background: theme.colors.background.paper,
                    borderBottom: `1px solid ${theme.colors.border.default}`,
                    fontSize: fontSize.xs,
                    fontWeight: 600,
                    color: mutedColor,
                    textTransform: 'uppercase',
                  }}
                >
                  <div>Creator</div>
                  <div>Matter</div>
                  <div>Videos</div>
                  <div>Duration</div>
                  <div>Total</div>
                </div>

                {/* Streamer Rows */}
                {streamerData.map((streamer, idx) => (
                  <div
                    key={`${streamer.streamerName}-${streamer.matterCode}-${idx}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 100px 120px 120px',
                      padding: `${spacing.sm} ${spacing.md}`,
                      borderBottom: `1px solid ${theme.colors.border.default}`,
                      alignItems: 'center',
                      fontSize: fontSize.sm,
                    }}
                  >
                    <div style={{ fontWeight: 500, color: textColor }}>{streamer.streamerName}</div>
                    <div>
                      <div style={{ color: textColor }}>{streamer.matterCode}</div>
                      <div style={{ fontSize: fontSize.sm, color: mutedColor }}>{streamer.clientName}</div>
                    </div>
                    <div style={{ color: textColor }}>{streamer.videoCount}</div>
                    <div style={{ color: textColor }}>{formatMinutes(streamer.totalMinutes)}</div>
                    <div style={{ fontWeight: 500, color: theme.colors.semantic.success.default }}>
                      {formatCurrency(streamer.totalCost)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Stack>
        </Card.Body>
      </Card>
    </Stack>
  );
}

export default BillingDashboard;
